import { Component, HostBinding, HostListener, inject, OnDestroy } from '@angular/core';
import { SolIconComponent } from '../../core/sol-icon/sol-icon.component';
import { AbstractComponent } from '../../abstract.component';
import { SolIcon } from '@sol/icons';
import { NavigationService } from '../../../services/navigation.service';
import { AuthenticationService } from '../../../services/authentication.service';
import { PopoverService, SolPopoverRef } from '../../../services/popover.service';
import { TimerTools } from '../../../tools/timer-tools';
import { HoverCloseTimer } from '../../hover-close-timer';
import { SolPopoverMenuContentComponent } from '../../popover/sol-popover-menu-content/sol-popover-menu-content.component';
import { SolMenuGroup } from '../../popover/sol-popover-menu-content/sol-popover-menu-content.state';
import { SolPopoverAlignment } from '../../popover/sol-popover/sol-popover.state';
import { NavigationTarget } from '../../../tools/router-tools';
import {
  SolNavbarExpandedEvent,
  SolNavbarHoverPreviewEvent,
  SolNavbarItemComponent,
} from '../sol-navbar-item/sol-navbar-item.component';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { SolRouteId } from '../../../models/sol-navigation';
import { SolButtonType } from '../../../models/core/sol-button';
import { SolLoaderComponent } from '../../core/sol-loader/sol-loader.component';
import { SolLoader } from '../../../models/core/sol-loader';
import { SolNavbarState } from './sol-navbar.state';
import { SolBadgeComponent } from '../../core/sol-badge/sol-badge.component';

@Component({
  selector: 'sol-navbar',
  imports: [SolIconComponent, SolNavbarItemComponent, TranslatePipe, SolLoaderComponent, SolBadgeComponent],
  templateUrl: './sol-navbar.component.html',
  host: { role: 'navigation', 'aria-label': 'Main navigation' },
})
export class SolNavbarComponent extends AbstractComponent<SolNavbarState> implements OnDestroy {
  protected readonly SolButtonType = SolButtonType;
  protected readonly SolIcon = SolIcon;
  protected readonly SolLoader = SolLoader;

  // Dependencies
  private readonly navigationService = inject(NavigationService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly popoverService = inject(PopoverService);

  // Computed
  protected readonly menu$ = this.compute(state => state.menu);
  protected readonly systemButtons$ = this.compute(state => state.systemButtons);
  protected readonly name$ = this.compute(state => state.name);
  protected readonly initials$ = this.compute(state => state.initials);
  protected readonly activeRouteIds$ = this.compute(state => state.activeRouteIds);
  protected readonly expandedIds$ = this.compute(state => state.expandedIds);
  protected readonly hostClasses$ = this.compute(state => state.hostClasses);
  protected readonly dockToggleIcon$ = this.compute(state => state.dockToggleIcon);
  protected readonly appDisplayName$ = this.compute(state => state.appDisplayName);
  protected readonly appDisplayIcon$ = this.compute(state => state.appDisplayIcon);
  protected readonly appsVisible$ = this.compute(state => state.appsVisible);
  protected readonly appsDropdownVisible$ = this.compute(state => state.appsDropdownVisible);
  protected readonly appHover$ = this.compute(state => state.appHover);
  protected readonly loading$ = this.compute(state => state.loading);

  // Cache
  protected popoverMenuRef?: SolPopoverRef<SolPopoverMenuContentComponent>;
  private hoverPreviewRouteId: SolRouteId | undefined;
  private readonly hoverClose = new HoverCloseTimer();

  constructor() {
    super(SolNavbarState.of());

    // Listen to navbar data
    this.navigationService.watch(nss => {
      const app = nss.apps.find(candidate => candidate.id === nss.appId && candidate.hasAuthority);
      const appMenuItems = nss.appMenu.flatMap(group => group.items);
      const appDisplayItem = appMenuItems.length === 1 ? appMenuItems[0] : undefined;
      const appsVisible = appMenuItems.length > 0;
      const appsDropdownVisible = appMenuItems.length > 1;
      const appDisplayName = app?.name ?? appDisplayItem?.name ?? 'sol-navbar.apps.choose';
      const appDisplayIcon = app?.icon ?? appDisplayItem?.icon;
      this.update(state =>
        state.withNavigation(
          app,
          nss.appMenu,
          appsVisible,
          appsDropdownVisible,
          appDisplayName,
          appDisplayIcon,
          nss.navbar.menu,
          nss.navbar.system,
          nss.navbar.account,
          nss.navbarVisible,
          nss.navbarDrawerOpen,
          nss.activeRouteStatus === 'loading',
        ),
      );
      // A frame after the mode lands, sliding may animate again.
      TimerTools.nextTick(() => this.update(state => state.withDrawerAnimated(true)));
    });
    this.navigationService.watchComputed(
      nss => nss.navbarItemCounts,
      navbarItemCounts => {
        this.update(state => state.withItemCounts(navbarItemCounts));
      },
    );
    this.navigationService.watchComputed(
      nss => nss.activeNavbarItems,
      activeNavbarItems => {
        const activeRouteIds = activeNavbarItems.map(item => item.id);
        this.update(state => state.withActiveNavbarItems(activeRouteIds));
      },
    );

    // Listen to name changes
    this.authenticationService.watchComputed(
      state => state.name || '',
      name => {
        this.update(state => state.withName(name));
      },
    );
  }

  @HostBinding('class')
  public get hostClass(): string {
    return this.hostClasses$();
  }

  ngOnDestroy(): void {
    this.hoverClose.cancel();
    this.popoverMenuRef?.close();
  }

  protected onClick(id: SolRouteId, newTab: boolean = false) {
    this.navigationService.navigate(id, {
      target: newTab ? NavigationTarget.NewTab : undefined,
    });
  }

  protected onSystemClick(id: SolRouteId, event: MouseEvent, newTab: boolean = false) {
    event.stopPropagation();

    // Navigate if no children
    const state = this.state;
    const shouldNavigate = state.system.find(item => item.id === id)!.children.length === 0;
    if (shouldNavigate) {
      this.onClick(id, newTab);
      return;
    }

    // Otherwise show menu
    const popoverMenu = state.systemMenus.get(id)!;
    this.showMenu(popoverMenu, event, false);
  }

  protected onAccountClick(event: MouseEvent) {
    this.showMenu(this.state.accountMenu, event, true);
  }

  protected onAppClick(event: MouseEvent) {
    this.update(state => state.withAppHover(true));
    this.showMenu(this.state.appMenu, event, true);
  }

  protected onToggleNavbar(event: MouseEvent) {
    event.stopPropagation();
    this.navigationService.updateNavbarVisible(!this.state.navbarVisible);
  }

  /**
   * The drawer closes on the way out, with a delay so a wobble across the edge does not snap
   * it shut. Opening it is the topbar's job.
   */
  @HostListener('mouseleave')
  protected onMouseLeave(): void {
    this.hoverClose.cancel();
    if (this.state.navbarVisible || !this.state.drawerOpen) {
      return;
    }
    this.hoverClose.schedule(() => this.navigationService.updateNavbarDrawerOpen(false));
  }

  @HostListener('mouseenter')
  protected onMouseEnter(): void {
    this.hoverClose.cancel();
  }

  protected onExpanded(event: SolNavbarExpandedEvent) {
    this.closePopoverMenu();
    this.update(state => {
      let expandedIds = state.expandedIds;
      const isExpanded = expandedIds.includes(event.id);
      if (isExpanded) {
        expandedIds = expandedIds.filter(id => id !== event.id);
      } else {
        expandedIds = [...expandedIds, event.id];
      }
      return state.withExpandedIds(expandedIds);
    });
  }

  protected onHoverPreview(event: SolNavbarHoverPreviewEvent): void {
    if (this.popoverMenuRef && this.hoverPreviewRouteId === event.id) {
      return;
    }

    this.closePopoverMenu();
    const popoverMenuRef = this.popoverService.showMenu(event.menu, actionId => this.onPreviewAction(actionId), {
      afterClose: () => {
        if (this.popoverMenuRef?.id === popoverMenuRef.id) {
          this.popoverMenuRef = undefined;
          this.hoverPreviewRouteId = undefined;
        }
      },
      align: SolPopoverAlignment.bottomLeft,
      bounds: event.bounds,
      boundsRef: event.boundsRef,
      closeOnHoverOut: true,
      fadeIn: false,
      gapX: 4,
      gapY: 0,
      width: 'auto',
    });
    this.popoverMenuRef = popoverMenuRef;
    this.hoverPreviewRouteId = event.id;
  }

  protected closePopoverMenu(): void {
    const popoverMenuRef = this.popoverMenuRef;
    this.popoverMenuRef = undefined;
    this.hoverPreviewRouteId = undefined;
    popoverMenuRef?.close();
  }

  private showMenu(menu: SolMenuGroup[], event: MouseEvent, parentWidth: boolean) {
    this.closePopoverMenu();
    this.popoverMenuRef = this.popoverService.showMenu(
      menu,
      actionId => {
        if (actionId === 'logout') {
          this.authenticationService.logout();
          return true;
        }
        this.onClick(actionId as SolRouteId);
        return true;
      },
      {
        afterClose: () => {
          this.update(state => state.withAppHover(false));
        },
        align: SolPopoverAlignment.bottomLeft,
        boundsEvent: event,
        width: parentWidth ? 'parent-grow' : 'auto',
      },
    );
  }

  private onPreviewAction(actionId: string): boolean {
    this.onClick(actionId as SolRouteId);
    return true;
  }
}
